import { useId } from 'react';

import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';
import type {
  ExecutionResultView,
  TaskFileChangeView,
} from '@/lib/api/generated/model';
import { runDuration, runOutcome } from '../executionHistory';
import { DRAWER_PROSE_CLASS } from '../prose';
import { ResultFiles } from './ResultFiles';
import { ResultLinks } from './ResultLinks';
import type { ForgeLinks } from '../forgeLinks';
import { RunArtifactLines, type RunArtifactLine } from './RunArtifactLines';

/**
 * What one run produced (spec 095 FR-008 – FR-015): its step and outcome in
 * the Executions tab's own words, then the summary, the verified links, and
 * the files it changed, in that order.
 *
 * The step is named by the identifier the run recorded, for the reason the
 * Executions tab gives: a run carries a snapshot precisely because the
 * workflow can be edited afterwards. Two tabs naming one run two ways would
 * be a bug; naming it the same way is the point.
 *
 * The summary renders with `linkPolicy="none"`: it is prose the agent wrote,
 * and an address in it may be a guess. The links that were checked against
 * the run's own output are the list beneath it, and those are anchors.
 */
export function ResultRunGroup({
  workspaceId,
  execution,
  name,
  files,
  filePaths,
  artifactLines = [],
  artifactRecordIncomplete = false,
  forgeLinks,
  isLast = false,
}: {
  workspaceId: string;
  execution: ExecutionResultView;
  /** The step as a reader knows it (`stepDisplayName`), never its id. */
  name: string;
  files: readonly TaskFileChangeView[];
  /** How a path the summary mentions reaches the file (FR-018). */
  filePaths?: MarkdownFilePaths;
  /** What this run created or pushed to on the forge (spec 119 FR-007). */
  artifactLines?: readonly RunArtifactLine[];
  /** Its worker counted more than reached the record (spec 119 FR-008). */
  artifactRecordIncomplete?: boolean;
  /** Addresses for the branches in `artifactLines`. */
  forgeLinks?: ForgeLinks;
  /** The oldest run: nothing below it for the rail to reach. */
  isLast?: boolean;
}) {
  const headingId = useId();
  const outcome = runOutcome(execution.status);
  const summary = execution.result?.summary.trim() ?? '';
  const links = execution.result?.links ?? [];
  const when = execution.ended_at ?? execution.started_at;
  const duration = runDuration(execution);
  return (
    <li className="grid grid-cols-[0.75rem_1fr] gap-x-3">
      <ResultMarker colourClass={outcome.colourClass} isLast={isLast} />
      <section
        aria-labelledby={headingId}
        className={`flex min-w-0 flex-col gap-3 ${isLast ? '' : 'pb-6'}`}
      >
        <div className="flex flex-col gap-0.5">
          <h3
            id={headingId}
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
          >
            <span className="text-foreground text-sm font-medium">{name}</span>
            <span className={`${outcome.colourClass} text-xs font-medium`}>
              {outcome.label}
            </span>
          </h3>
          {/*
            When, and how long. The relative form is what a reader scanning
            down the list wants; the exact moment is one hover away. Ended
            before started: the result belongs to the run's end, and a run
            still going has only its start to offer.
          */}
          {when || duration ? (
            <p className="text-muted-foreground flex flex-wrap items-baseline gap-x-2 text-xs">
              {when ? (
                <time dateTime={when} title={formatExactTime(when)}>
                  {formatRelativeTime(when)}
                </time>
              ) : null}
              {duration ? <span>took {duration}</span> : null}
            </p>
          ) : null}
        </div>
        {summary ? (
          <MarkdownLazy
            content={summary}
            className={DRAWER_PROSE_CLASS}
            linkPolicy="none"
            filePaths={filePaths}
          />
        ) : (
          <p className="text-muted-foreground text-sm">
            No summary recorded for this run.
          </p>
        )}
        <RunArtifactLines
          lines={artifactLines}
          incomplete={artifactRecordIncomplete}
          links={forgeLinks}
        />
        <ResultLinks links={links} />
        <ResultFiles workspaceId={workspaceId} files={files} />
      </section>
    </li>
  );
}

/**
 * The rail: a dot in the run's outcome colour, and a line down to the next
 * run's dot. The same track the Executions tab draws, so the two tabs read as
 * two views of one history — what happened there, what came out of it here.
 * Decorative: the heading beside it says the outcome in words.
 */
function ResultMarker({
  colourClass,
  isLast,
}: {
  colourClass: string;
  isLast: boolean;
}) {
  return (
    <span aria-hidden="true" className={`relative ${colourClass}`}>
      <span className="bg-current absolute top-1.5 left-1/2 size-2 -translate-x-1/2 rounded-full" />
      {isLast ? null : (
        // From this dot's bottom edge (0.375rem + 0.5rem) to the next dot's top
        // edge, which sits 0.375rem into the next item: the cell's full height
        // less half a rem.
        <span className="bg-border absolute top-[0.875rem] left-1/2 h-[calc(100%-0.5rem)] w-px -translate-x-1/2" />
      )}
    </span>
  );
}
